import { useCallback, useEffect, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router"
import { motion, AnimatePresence } from "motion/react"
import { ArrowLeft } from "@untitledui/icons"

import { ProtectedRoute } from "@/lib/auth/protected-route"
import { ALL_ROLES } from "@/lib/team/roles"
import { AppLayout } from "@/components/layout/app-layout"
import { useEstimate } from "@/lib/api/use-estimates"
import type { ApiEstimate } from "@/lib/api/types"
import { StepOperations } from "@/components/estimates/full-estimate/step-operations"
import { EvidenceCanvas } from "@/components/estimates/photo-canvas/evidence-canvas"
import { ConfirmDialog } from "@/components/estimates/wizard-shared/confirm-dialog"
import { cx } from "@/lib/utils/cx"

/**
 * Estimate edit page. Two tabs, ?tab=damage|operations.
 *
 * Damage is the evidence canvas: the photos big, the components found on
 * each one under it, the estimate beside them — one link, edited from
 * either end, saved in one PUT. Operations reuses the wizard's step
 * unchanged. Leaving the canvas with unsaved changes asks first.
 */
type Tab = "damage" | "operations"

function EstimateEditPageInner() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const estimateId = id ? Number(id) : null
  const { data: estimate, isLoading, error } = useEstimate(estimateId)

  const tab: Tab = params.get("tab") === "operations" ? "operations" : "damage"
  const [dirty, setDirty] = useState(false)
  const [leaving, setLeaving] = useState<null | (() => void)>(null)

  const guard = useCallback(
    (go: () => void) => {
      if (dirty) setLeaving(() => go)
      else go()
    },
    [dirty],
  )

  useEffect(() => {
    if (!dirty) return
    const onBefore = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener("beforeunload", onBefore)
    return () => window.removeEventListener("beforeunload", onBefore)
  }, [dirty])

  const goTab = (t: Tab) => guard(() => setParams({ tab: t }))
  const goDetail = () => guard(() => navigate(`/estimates/${estimateId}`))

  if (isLoading)
    return (
      <Page>
        <Skeleton />
      </Page>
    )
  if (error || !estimate || estimateId == null) {
    return (
      <Page>
        <NotFound onBack={() => navigate("/estimates")} />
      </Page>
    )
  }

  return (
    <Page>
      <Link
        to={`/estimates/${estimateId}`}
        onClick={(e) => {
          if (dirty) { e.preventDefault(); goDetail() }
        }}
        className="mb-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-zinc-500 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30 rounded-sm"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to estimate
      </Link>

      <EditHeader estimate={estimate} tab={tab} onTab={goTab} />

      <div className="mt-5">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {tab === "damage" ? (
              <EvidenceCanvas estimate={estimate} onDirtyChange={setDirty} />
            ) : (
              <div className="mx-auto max-w-5xl">
                <StepOperations
                  estimateId={estimateId}
                  onBack={() => setParams({ tab: "damage" })}
                  onNext={() => navigate(`/estimates/${estimateId}`)}
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <ConfirmDialog
        open={leaving != null}
        title="Leave without saving?"
        body="The changes to the damage lines and photos will be lost."
        cancelLabel="Stay"
        confirmLabel="Leave"
        destructive
        onCancel={() => setLeaving(null)}
        onConfirm={() => {
          const go = leaving
          setLeaving(null)
          setDirty(false)
          go?.()
        }}
      />
    </Page>
  )
}

function Page({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-zinc-50/40">
      <div className="mx-auto max-w-[1600px] px-4 py-5 md:px-6 md:py-6">{children}</div>
    </div>
  )
}

function EditHeader({
  estimate,
  tab,
  onTab,
}: {
  estimate: ApiEstimate
  tab: Tab
  onTab: (t: Tab) => void
}) {
  const v = estimate.vehicle
  const vehicle = [v?.year, v?.make, v?.model].filter(Boolean).join(" ") || "Vehicle"
  // Consumer-intake leads carry the contact on metadata; the BE has no customer join here.
  const who = (estimate.metadata as { contact?: { name?: string } } | null)?.contact?.name

  return (
    <div className="flex flex-col gap-2 border-b border-zinc-200 md:flex-row md:items-end md:justify-between">
      <div className="min-w-0 pb-3">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500">
          Editing estimate <span className="tabular-nums">#{estimate.id}</span>
        </p>
        <h1 className="mt-0.5 truncate text-[22px] font-semibold tracking-tight text-zinc-900">
          {who ? `${who} · ${vehicle}` : vehicle}
        </h1>
      </div>
      <Tabs tab={tab} onTab={onTab} />
    </div>
  )
}

function Tabs({ tab, onTab }: { tab: Tab; onTab: (t: Tab) => void }) {
  return (
    <div role="tablist" aria-label="Edit sections" className="-mb-px flex gap-5">
      {(["damage", "operations"] as const).map((value) => {
        const active = value === tab
        return (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onTab(value)}
            className={cx(
              "relative h-11 text-[13.5px] font-semibold transition-colors duration-150",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30 rounded-sm",
              active ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-800",
            )}
          >
            {value === "damage" ? "Damage" : "Operations"}
            {active && (
              <motion.span
                layoutId="edit-tab"
                className="absolute inset-x-0 bottom-0 h-0.5 bg-zinc-900"
                transition={{ type: "spring", stiffness: 480, damping: 38 }}
              />
            )}
          </button>
        )
      })}
    </div>
  )
}

function Skeleton() {
  return (
    <>
      <div className="mb-3 h-4 w-28 rounded bg-zinc-100" />
      <div className="h-16 border-b border-zinc-200" />
      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="h-[520px] animate-pulse rounded-xl border border-zinc-200/70 bg-white" />
        <div className="h-96 animate-pulse rounded-xl border border-zinc-200/70 bg-white" />
      </div>
    </>
  )
}

function NotFound({ onBack }: { onBack: () => void }) {
  useEffect(() => {
    onBack()
  }, [onBack])
  return null
}

export default function EstimateEditPage() {
  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <EstimateEditPageInner />
      </AppLayout>
    </ProtectedRoute>
  )
}
